import React, { useState, useContext, useEffect } from 'react';
import style from './InterestsPage.module.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import InterestChoiceCard from '../../components/InterestChoiceCard/InterestChoiceCard';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthUser } from '../../hooks/useAuthUser';
import { Link, useNavigate } from 'react-router-dom';
import Checkbox from '../../components/CheckBox/CheckBox';
import RadioButton from '../../components/RadioButton/RadioButton';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import FormInput from '../../components/FormInput/FormInput';
import {
	MdPerson,
	MdFitnessCenter,
	MdTimer,
	MdTrendingUp,
	MdArrowBack,
	MdCheck,
	MdAutoAwesome,
} from 'react-icons/md';

const interestsArray = [
	'Running', 'Home Workouts', 'Swimming', 'Lifting', 'Yoga', 'Cardio',
	'Boxing', 'Pilates', 'HIIT', 'CrossFit', 'Dance', 'Martial Arts',
	'Functional Training', 'Cycling', 'TRX Training', 'Calisthenics',
	'Pelvic Floor',
];

const equipmentArray = [
	'Dumbbells', 'Treadmill', 'Resistance Bands', 'Barbell', 'Kettlebell',
	'Pull-Up Bar', 'Yoga Mat', 'Rowing Machine', 'Exercise Bike',
	'Medicine Ball', 'Bench Press', 'Jump Ropes', 'Stability Ball',
	'Battle Ropes', 'Cable Machine',
];

const ageRangeArray = ['13 - 17', '18 - 30', '31 - 50', '51+'];
const genderArray = ['Male', 'Female', 'Other'];
const fitnessLevelArray = ['Beginner', 'Intermediate', 'Advanced'];
const styleArray = ['Short and Intense', 'Long and moderate'];
const durationArray = ['30 mins', '45 mins', '1 hr'];
const API_URL = process.env.REACT_APP_API_URL;

const InterestsPage = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate();
	useTheme();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const { dbUser } = useContext(UserContext);
	const [selectedInterests, setSelectedInterests] = useState({
		type: 'bodyweight',
		workoutInterests: [],
		workoutEquipments: [],
		ageRange: '',
		gender: '',
		level: '',
		style: '',
		duration: '',
		goal: '',
		additionalText: '',
	});

	const steps = [
		{ id: 1, title: 'Basic Info', icon: MdPerson },
		{ id: 2, title: 'Workout Type', icon: MdFitnessCenter },
		{ id: 3, title: 'Preferences', icon: MdTimer },
		{ id: 4, title: 'Goals', icon: MdTrendingUp },
	];

	const addInterests = () => {
		const { level, workoutInterests, style, duration, goal } = selectedInterests;
		if (!level) { toast('Please select your fitness level.'); return; }
		if (!workoutInterests.length) { toast('Please choose at least one workout interest.'); return; }
		if (!style) { toast('Please choose a workout style.'); return; }
		if (!duration) { toast('Please choose a workout duration.'); return; }
		if (!goal) { toast('Please enter your workout goal.'); return; }

		setIsSubmitting(true);

		if (user) {
			axios
				.post(`${API_URL}/interests/${user.id}`, selectedInterests)
				.then(() => {
					navigate('/generated-stryds');
				})
				.catch(err => {
					console.error(err);
					toast('Something went wrong. Please try again.');
					setIsSubmitting(false);
				});
		}
	};

	const handleInterestChange = (interest) => (isChecked) => {
		setSelectedInterests(prev => ({
			...prev,
			workoutInterests: isChecked
				? [...prev.workoutInterests, interest]
				: prev.workoutInterests.filter(i => i !== interest),
		}));
	};

	const handleEquipmentChange = (equipment) => (isChecked) => {
		setSelectedInterests(prev => ({
			...prev,
			workoutEquipments: isChecked
				? [...prev.workoutEquipments, equipment]
				: prev.workoutEquipments.filter(e => e !== equipment),
		}));
	};

	const handleLevelChange = (level) => () => {
		setSelectedInterests(prev => ({ ...prev, level }));
	};

	const handleAgeRangeChange = (ageRange) => () => {
		setSelectedInterests(prev => ({ ...prev, ageRange }));
	};

	const handleGenderChange = (gender) => () => {
		setSelectedInterests(prev => ({ ...prev, gender }));
	};

	const handleStyleChange = (style) => () => {
		setSelectedInterests(prev => ({ ...prev, style }));
	};

	const handleDurationChange = (duration) => () => {
		setSelectedInterests(prev => ({ ...prev, duration }));
	};

	const handleGoalTextChange = event => {
		setSelectedInterests(prev => ({ ...prev, goal: event.target.value }));
	};

	const handleAdditionalTextChange = event => {
		setSelectedInterests(prev => ({ ...prev, additionalText: event.target.value }));
	};

	const nextStep = () => {
		const { level, workoutInterests, workoutEquipments, style, duration, type } = selectedInterests;
		if (currentStep === 1 && !level) {
			toast('Please select a fitness level to continue.'); return;
		}
		if (currentStep === 2) {
			if (!workoutInterests.length) {
				toast('Please select at least one workout interest.'); return;
			}
			if (type === 'equipments' && !workoutEquipments.length) {
				toast('Please select at least one available equipment or switch to Bodyweight.'); return;
			}
		}
		if (currentStep === 3 && (!style || !duration)) {
			toast('Please select both a workout style and duration.'); return;
		}
		if (currentStep < 4) setCurrentStep(currentStep + 1);
	};

	const prevStep = () => {
		if (currentStep > 1) setCurrentStep(currentStep - 1);
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [currentStep]);

	const getCompletionItems = () => {
		const items = [];
		if (selectedInterests.level) items.push(selectedInterests.level);
		if (selectedInterests.workoutInterests.length) items.push(`${selectedInterests.workoutInterests.length} interests`);
		if (selectedInterests.style) items.push(selectedInterests.style);
		if (selectedInterests.duration) items.push(selectedInterests.duration);
		if (selectedInterests.goal) items.push('Goal set');
		return items;
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<div className={style.StepContent} key="step-1">
						<div className={style.StepSection}>
							<h3 className={style.StepTitle}>Tell us about yourself</h3>
							<p className={style.StepDescription}>This helps us create a personalized routine just for you.</p>
							<div className={style.FormSection}>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>Age Range</label>
									<div className={style.RadioGroup}>
										{ageRangeArray.map((ageRange) => (
											<RadioButton key={ageRange} label={ageRange} value={ageRange} name="age"
												isChecked={selectedInterests.ageRange === ageRange}
												onRadioChange={handleAgeRangeChange(ageRange)} />
										))}
									</div>
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>Gender</label>
									<div className={style.RadioGroup}>
										{genderArray.map((gender) => (
											<RadioButton key={gender} label={gender} value={gender} name="gender"
												isChecked={selectedInterests.gender === gender}
												onRadioChange={handleGenderChange(gender)} />
										))}
									</div>
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										Fitness Level <span className={style.Required}>*</span>
									</label>
									<div className={style.RadioGroup}>
										{fitnessLevelArray.map((level) => (
											<RadioButton key={level} label={level} value={level} name="levels"
												isChecked={selectedInterests.level === level}
												onRadioChange={handleLevelChange(level)} />
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				);

			case 2:
				return (
					<div className={style.StepContent} key="step-2">
						<div className={style.StepSection}>
							<h3 className={style.StepTitle}>Choose your workout style</h3>
							<p className={style.StepDescription}>Select the type of workouts you enjoy and have access to.</p>
							<div className={style.FormSection}>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>Workout Type</label>
									<div className={style.ChoiceCardsWrapper}>
										<InterestChoiceCard
											Title="Bodyweight" Text="Workout using just your body weight."
											click={() => setSelectedInterests(prev => ({ ...prev, type: 'bodyweight' }))}
											selected={selectedInterests.type === 'bodyweight'} />
										<InterestChoiceCard
											Title="With Equipment" Text="Enhance your workouts with equipment."
											click={() => setSelectedInterests(prev => ({ ...prev, type: 'equipments' }))}
											selected={selectedInterests.type === 'equipments'} />
									</div>
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										Workout Interests <span className={style.Required}>*</span>
									</label>
									<div className={style.InterestsGrid}>
										{interestsArray.map((interest) => (
											<Checkbox key={interest} label={interest}
												isChecked={selectedInterests.workoutInterests.includes(interest)}
												onCheckboxChange={handleInterestChange(interest)} />
										))}
									</div>
								</div>
								{selectedInterests.type === 'equipments' && (
									<div className={style.FormGroup}>
										<label className={style.FormLabel}>Available Equipment</label>
										<div className={style.InterestsGrid}>
											{equipmentArray.map((equipment) => (
												<Checkbox key={equipment} label={equipment}
													isChecked={selectedInterests.workoutEquipments.includes(equipment)}
													onCheckboxChange={handleEquipmentChange(equipment)} />
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				);

			case 3:
				return (
					<div className={style.StepContent} key="step-3">
						<div className={style.StepSection}>
							<h3 className={style.StepTitle}>Workout preferences</h3>
							<p className={style.StepDescription}>Customize your routine to fit your schedule and style.</p>
							<div className={style.FormSection}>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										Workout Style <span className={style.Required}>*</span>
									</label>
									<div className={style.RadioGroup}>
										{styleArray.map((s) => (
											<RadioButton key={s} label={s} value={s} name="styles"
												isChecked={selectedInterests.style === s}
												onRadioChange={handleStyleChange(s)} />
										))}
									</div>
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										Workout Duration <span className={style.Required}>*</span>
									</label>
									<div className={style.RadioGroup}>
										{durationArray.map((d) => (
											<RadioButton key={d} label={d} value={d} name="durations"
												isChecked={selectedInterests.duration === d}
												onRadioChange={handleDurationChange(d)} />
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				);

			case 4:
				return (
					<div className={style.StepContent} key="step-4">
						<div className={style.StepSection}>
							<h3 className={style.StepTitle}>Your fitness goals</h3>
							<p className={style.StepDescription}>Help us understand what you want to achieve.</p>
							<div className={style.FormSection}>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										What are your current workout goals? <span className={style.Required}>*</span>
									</label>
									<FormInput HeadingText="" placeholder="e.g. Build upper body strength, improve cardio endurance..."
										change={handleGoalTextChange} width="100%" showIsEmpty={true} />
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>Additional Information</label>
									<FormInput HeadingText="" placeholder="Any injuries, limitations, or specific requests..."
										change={handleAdditionalTextChange} width="100%" showIsEmpty={true} />
								</div>

								{getCompletionItems().length > 0 && (
									<div className={style.SummaryCard}>
										<div className={style.SummaryHeader}>
											<MdAutoAwesome className={style.SummaryIcon} />
											<span className={style.SummaryTitle}>Your Profile Summary</span>
										</div>
										<div className={style.SummaryTags}>
											{getCompletionItems().map((item, i) => (
												<span key={i} className={style.SummaryTag}>
													<MdCheck className={style.TagCheck} />
													{item}
												</span>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	const isPremium = dbUser.userPlan === 'calcium-monthly'
		|| dbUser.userPlan === 'calcium-quarterly'
		|| dbUser.userPlan === 'calcium-annually';

	return (
		<div className={style.InterestsPage}>
			{isPremium ? (
				<>
					<div className={style.Header}>
						<button className={style.BackButton} onClick={() => navigate(-1)}>
							<MdArrowBack />
							<span>Back</span>
						</button>
						<div className={style.HeaderContent}>
							<div className={style.AiBadge}>
								<MdAutoAwesome />
								<span>AI-Powered</span>
							</div>
							<h1 className={style.PageTitle}>Generate Your Routine</h1>
							<p className={style.PageDescription}>
								Answer a few questions and we'll craft a personalized workout routine for you.
							</p>
						</div>
					</div>

					<div className={style.ProgressContainer}>
						<div className={style.ProgressBar}>
							<div className={style.ProgressFill}
								style={{ width: `${(currentStep / steps.length) * 100}%` }} />
						</div>
						<div className={style.StepsIndicator}>
							{steps.map((step) => (
								<div key={step.id}
									className={`${style.StepIndicator} ${currentStep >= step.id ? style.Active : ''} ${currentStep === step.id ? style.Current : ''}`}>
									<div className={style.StepIcon}>
										{currentStep > step.id ? <MdCheck /> : <step.icon />}
									</div>
									<span className={style.StepText}>{step.title}</span>
								</div>
							))}
						</div>
					</div>

					{renderStepContent()}

					<div className={style.NavigationButtons}>
						{currentStep > 1 && (
							<SecondaryButton text="Back" click={prevStep} />
						)}
						<div className={style.NavSpacer} />
						{currentStep < 4 ? (
							<PrimaryButton text="Continue" click={nextStep} />
						) : (
							<PrimaryButton
								text={isSubmitting ? 'Generating...' : 'Generate Routine'}
								click={addInterests}
								loading={isSubmitting}
							/>
						)}
					</div>
				</>
			) : (
				<div className={style.UpgradeInfoWrapper}>
					<div className={style.UpgradeContent}>
						<MdAutoAwesome className={style.UpgradeIcon} />
						<h2>Upgrade to Calcium Plus</h2>
						<p>Generate personalized AI-powered workout routines tailored to your goals and preferences.</p>
						<Link to="/upgrade">
							<PrimaryButton text="Upgrade Now" click={() => null} />
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

export default InterestsPage;
